import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Bar } from 'react-chartjs-2';
import Card from '@mui/material/Card';
import MDBox from '../components/MDBox';
import Divider from '@mui/material/Divider';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MDTypography from '../components/MDTypography';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { db, collection, query, getDocs, where, doc, getDoc } from '../firebaseConfig';
import { usePeriod } from '../components/PeriodContext';

const TeamEquityGraph = ({ userID, tradeAccountID = 'default' }) => {
  const { selectedPeriod, selectedYear, selectedMonth, selectedTag } = usePeriod();
  const [trades, setTrades] = useState([]);
  const [year, setYear] = useState(selectedYear || new Date().getFullYear());
  const [accountName, setAccountName] = useState(tradeAccountID);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  useEffect(() => {
    const fetchAccountName = async () => {
      if (tradeAccountID === 'default') {
        setAccountName('Default');
        return;
      }
      try {
        const accountDoc = await getDoc(doc(db, 'Users', userID, 'tradeAccounts', tradeAccountID));
        if (accountDoc.exists()) {
          setAccountName(accountDoc.data().name || tradeAccountID);
        }
      } catch (error) {
        console.error('TeamEquityGraph: Error fetching account name:', error);
      }
    };
    fetchAccountName();
  }, [userID, tradeAccountID]);

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        setLoading(true);
        const tradesPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
        const tradesQuery = query(
          collection(db, tradesPath),
          where('status', '==', 'Closed')
        );
        const tradesSnapshot = await getDocs(tradesQuery);
        const tradesData = tradesSnapshot.docs.map((doc) => ({
          id: doc.id,
          accountID: tradeAccountID,
          date: doc.data().date ? new Date(doc.data().date) : new Date(),
          ...doc.data(),
        }));
        console.log('TeamEquityGraph: Fetched trades for userID:', userID, tradesData);
        setTrades(tradesData);
      } catch (error) {
        console.error('TeamEquityGraph: Error fetching trades:', error);
        setTrades([]);
      } finally {
        setLoading(false);
      }
    };

    if (userID) {
      fetchTrades();
    } else {
      setTrades([]);
      setLoading(false);
    }
  }, [userID, tradeAccountID]);

  const filterTrades = () => {
    return trades.filter((trade) => {
      const tradeDate = new Date(trade.date);
      const matchesPeriod = () => {
        if (selectedPeriod === 'today') {
          return tradeDate.toDateString() === new Date().toDateString();
        } else if (selectedPeriod === 'this-week') {
          return (
            tradeDate.getWeek() === new Date().getWeek() &&
            tradeDate.getFullYear() === new Date().getFullYear()
          );
        } else if (selectedPeriod === 'this-month') {
          return (
            tradeDate.getMonth() === new Date().getMonth() &&
            tradeDate.getFullYear() === new Date().getFullYear()
          );
        } else if (selectedPeriod === 'this-year') {
          return tradeDate.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'pick-month') {
          return tradeDate.getMonth() + 1 === selectedMonth && tradeDate.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-year') {
          return tradeDate.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-month-year') {
          return tradeDate.getMonth() + 1 === selectedMonth && tradeDate.getFullYear() === selectedYear;
        } else {
          return true; // all-time
        }
      };

      const matchesTag =
        selectedTag === 'all' || (Array.isArray(trade.tags) && trade.tags.includes(selectedTag));
      return matchesPeriod() && matchesTag;
    });
  };

  const calculateEquity = (trades, year) => {
    const monthlyEquity = Array(12).fill(0);

    trades.forEach((trade) => {
      const tradeDate = new Date(trade.date);
      if (tradeDate.getFullYear() === year && trade.status === 'Closed') {
        const month = tradeDate.getMonth();
        monthlyEquity[month] += parseFloat(trade.returns) || 0;
      }
    });

    return monthlyEquity;
  };

  const filteredTrades = filterTrades();
  const monthlyEquity = calculateEquity(filteredTrades, year);

  const data = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Equity Profit/Loss',
        data: monthlyEquity,
        backgroundColor: monthlyEquity.map((value) =>
          value >= 0 ? theme.palette.success.main + '40' : theme.palette.error.main + '40'
        ),
        borderColor: monthlyEquity.map((value) =>
          value >= 0 ? theme.palette.success.main : theme.palette.error.main
        ),
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return `$${value}`;
          },
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context) {
            return `Profit/Loss: $${context.parsed.y.toFixed(2)}`;
          },
        },
      },
    },
  };

  const handleYearChange = (event) => {
    setYear(event.target.value);
  };

  const tooltipContent = `The Equity Profit/Loss Analysis displays monthly profit/loss totals, with green bars indicating profitable months and red bars showing losses. Hover over any bar to view detailed stats for that month. Use the year selector to compare performance across years and track monthly trends.`;

  if (loading) {
    return (
      <Card style={{ minHeight: '300px' }}>
        <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
          <MDTypography variant="caption" color="text">
            Loading trades...
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <AssessmentIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Monthly Equity Profit/Loss {tradeAccountID === 'default' ? '' : `(${accountName})`}
            </MDTypography>
            <Tooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': { color: theme.palette.background.paper },
                  },
                },
              }}
            >
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </Tooltip>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="center" mt={2}>
          <Select value={year} onChange={handleYearChange}>
            {[2023, 2024, 2025, 2026].map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </MDBox>
        <MDBox height="100%">
          <Bar data={data} options={chartOptions} />
        </MDBox>
      </MDBox>
    </Card>
  );
};

TeamEquityGraph.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string,
};

TeamEquityGraph.defaultProps = {
  tradeAccountID: 'default',
};

export default TeamEquityGraph;